import { auth } from '../firebase';
import {
  ExercisePopupsSettings,
  ExercisePopupsSettingsUpdate,
  ExerciseTypeInfo,
  ExercisePopupStats,
  DEFAULT_EXERCISE_POPUPS_SETTINGS,
} from '../types/exercisePopups';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`;

interface ExerciseTypesResponse {
  types: ExerciseTypeInfo[];
}

class ExercisePopupsService {
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const token = await user.getIdToken();
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }

    return response.json();
  }

  /** Exercise pop-up settings for a device (defaults when nothing is stored yet) */
  async getSettings(deviceId: string): Promise<ExercisePopupsSettings> {
    try {
      return await this.request<ExercisePopupsSettings>(`/devices/${deviceId}/exercise-popups/`);
    } catch {
      return DEFAULT_EXERCISE_POPUPS_SETTINGS;
    }
  }

  /** Partial update; exerciseTypes replaces the list ([] = every type) */
  async updateSettings(
    deviceId: string,
    updates: ExercisePopupsSettingsUpdate
  ): Promise<ExercisePopupsSettings> {
    return this.request<ExercisePopupsSettings>(`/devices/${deviceId}/exercise-popups/`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  /** Active exercise types of the global content catalog */
  async getExerciseTypes(): Promise<ExerciseTypeInfo[]> {
    const response = await this.request<ExerciseTypesResponse>('/content/exercise-types');
    return response.types;
  }

  /** Pop-up statistics: lifetime totals, last `days` daily buckets, newest results */
  async getStats(deviceId: string, days: number = 14, recentLimit: number = 50): Promise<ExercisePopupStats> {
    return this.request<ExercisePopupStats>(
      `/devices/${deviceId}/exercise-popups/stats?days=${days}&recent_limit=${recentLimit}`
    );
  }
}

export const exercisePopupsService = new ExercisePopupsService();
